import {
    faCirclePlay,
    faEllipsisVertical,
    faTrash,
    faUpRightAndDownLeftFromCenter,
} from '@fortawesome/pro-regular-svg-icons';
import {faPlay} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Dropdown, Flex, Image} from 'antd';
import {useCallback, useMemo, useState} from 'react';

import {gray8} from '../../../../css/var-export.scss';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {classNames} from '../../../../util/css';
import {usePreviewVideoController} from '../review-preview-video';
import {applyPreviewVideoVisibleChange} from '../review-preview-video-types';

import {ReviewAttachmentType, ReviewAttachmentTypeEnum} from './review-form-type';
import * as styles from './review-form.scss';

type PropsType = {
    attachment: ReviewAttachmentType;
    onDelete: () => void;
};

function formatDuration(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return '';
    }

    const fullSeconds = Math.round(seconds);
    const minutes = Math.floor(fullSeconds / 60);
    const restSeconds = String(fullSeconds % 60).padStart(2, '0');

    return `${minutes}:${restSeconds}`;
}

export function AttachmentTile(props: PropsType): JSX.Element {
    const {attachment, onDelete} = props;
    const {getLocalizedString} = useLocale();
    const isVideo = attachment.type === ReviewAttachmentTypeEnum.video;
    const [duration, setDuration] = useState<string>('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const previewSrc = attachment.previewSrc || attachment.src;
    const {previewClassName, renderEmptyPreviewToolbar, renderVideo, stop} = usePreviewVideoController();

    const handlePreviewVisibleChange = useCallback(
        (visible: boolean) => {
            applyPreviewVideoVisibleChange(visible, {setOpen: setPreviewOpen, stop});
        },
        [stop]
    );

    const renderPreviewVideo = useCallback(() => {
        if (!previewOpen) {
            return null;
        }

        return renderVideo(attachment.src);
    }, [attachment.src, previewOpen, renderVideo]);

    const imagePreview = useMemo(
        () =>
            isVideo
                ? {
                      className: previewClassName,
                      visible: previewOpen,
                      onVisibleChange: handlePreviewVisibleChange,
                      imageRender: renderPreviewVideo,
                      toolbarRender: renderEmptyPreviewToolbar,
                  }
                : {
                      visible: previewOpen,
                      onVisibleChange: handlePreviewVisibleChange,
                  },
        [
            handlePreviewVisibleChange,
            isVideo,
            previewClassName,
            previewOpen,
            renderEmptyPreviewToolbar,
            renderPreviewVideo,
        ]
    );

    function openPreview(event?: React.MouseEvent) {
        event?.stopPropagation();
        setPreviewOpen(true);
    }

    return (
        <div
            className={styles.ReviewForm_attachmentItem}
            onClick={openPreview}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openPreview();
                }
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            role="button"
            tabIndex={0}
        >
            {isVideo ? (
                <>
                    <video
                        className={styles.ReviewForm_attachmentPreview}
                        muted
                        onLoadedMetadata={(event) => {
                            setDuration(formatDuration(event.currentTarget.duration));
                        }}
                        preload="metadata"
                        src={previewSrc}
                    />
                    <Image
                        alt=""
                        className={styles.ReviewForm_attachmentPreviewHidden}
                        preview={imagePreview}
                        src={previewSrc}
                    />
                </>
            ) : (
                <Image alt="" className={styles.ReviewForm_attachmentPreview} preview={imagePreview} src={previewSrc} />
            )}

            {isVideo && (
                <>
                    <Button className={styles.ReviewForm_attachmentPlayButton} onClick={openPreview} type="text">
                        <FontAwesomeIcon className={styles.ReviewForm_attachmentPlayIcon} icon={faPlay} />
                    </Button>
                    {duration && <span className={styles.ReviewForm_attachmentDuration}>{duration}</span>}
                </>
            )}

            <Flex
                className={classNames(
                    styles.ReviewForm_attachmentOverlay,
                    ...(isHovered ? [styles.ReviewForm_attachmentOverlay_visible] : [])
                )}
            >
                <Dropdown
                    className={styles.ReviewForm_attachmentMenuDropdown}
                    menu={{
                        items: [
                            {
                                key: 'delete',
                                icon: <FontAwesomeIcon color={gray8} height={16} icon={faTrash} width={16} />,
                                label: getLocalizedString('BUTTON__DELETE'),
                                onClick: ({domEvent}) => {
                                    domEvent.stopPropagation();
                                    onDelete();
                                },
                            },
                            {
                                key: 'view',
                                icon: (
                                    <FontAwesomeIcon
                                        color={gray8}
                                        height={16}
                                        icon={isVideo ? faCirclePlay : faUpRightAndDownLeftFromCenter}
                                        width={16}
                                    />
                                ),
                                label: getLocalizedString(
                                    isVideo
                                        ? 'REVIEW__FORM__FIELD_ATTACHMENTS__VIEW_VIDEO'
                                        : 'REVIEW__FORM__FIELD_ATTACHMENTS__VIEW_PHOTO'
                                ),
                                onClick: ({domEvent}) => {
                                    domEvent.stopPropagation();
                                    openPreview();
                                },
                            },
                        ],
                    }}
                    placement="bottomLeft"
                    trigger={['click']}
                >
                    <Button onClick={(event) => event.stopPropagation()} type="text">
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                    </Button>
                </Dropdown>
            </Flex>
        </div>
    );
}
